import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { QueryKeys } from "@/constants/query-keys";
import { useGetAlternativePlans } from "@/hooks/requests/plan/use-get-alternative-plans";
import { useSearchParams } from "@/hooks/router/use-search-params";
import { IonSkeletonText, useIonViewWillLeave } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlternativePlanError } from "../../components/alternative-plan-error";
import { NoMealsEmptyState } from "../../components/no-meals-empty-state";
import { SwapPlanFilters } from "../../components/swap-plan-filters";
import { SwapPlanPageContainer } from "../../components/swap-plan-page-container";
import { SwapPlanSearch } from "../../components/swap-plan-search";
import { SwapPlanSwiper } from "../../components/swap-plan-swiper";
import { SwipeMappingMode } from "../../components/tinder-card-swiper";
import { useSwapPlanData } from "../../hooks/use-swap-plan-data";
import {
  getEffectiveFilter,
  getEffectiveSubFilter,
  parseParamList,
} from "../../utils/swap-plan-default-filter";

const SWIPE_MAPPING_MODE: SwipeMappingMode = "index";

export default function SwapPlanPage() {
  const searchParams = useSearchParams();
  const pageRef = useRef<HTMLElement>(null);

  const { getSwapPlanData, getSwapPlanRawData } = useSwapPlanData();

  const rawData = getSwapPlanRawData();
  const [mealPlan, setMealPlan] = useState(() => getSwapPlanData());

  const mealData: Meal | null = mealPlan?.meal ?? null;
  const mealTime = mealPlan?.mealTime;
  const mealDay = mealPlan?.mealDay;
  const mealIndex = mealPlan?.mealIndex;
  const targetDate = mealPlan?.targetDate;
  const isExtraMeal = mealPlan?.isExtraMeal ?? false;

  const filter = getEffectiveFilter(searchParams);
  const subFilter = getEffectiveSubFilter(searchParams);
  const fromBrands = parseParamList(searchParams, "fromBrand");
  const searchTerm = (searchParams.get("search") ?? "").trim();
  const swiperResetSignal = `${filter ?? ""}|${subFilter ?? ""}|${fromBrands.join(",")}|${searchTerm}`;

  const queryClient = useQueryClient();

  useIonViewWillLeave(
    useCallback(() => {
      // Preserve the cache when the user is leaving to view a meal detail
      // from within the search modal — they'll return here and expect the
      // previous results without a refetch.
      const isReopenPending =
        localStorage.getItem(LocalStorageKeys.SWAP_PLAN_REOPEN_SEARCH_MODAL) === "true";
      if (isReopenPending) {
        return;
      }
      queryClient
        .cancelQueries({
          queryKey: [QueryKeys.PLAN_ALTERNATIVE],
        })
        .then(() => {
          queryClient.removeQueries({
            queryKey: [QueryKeys.PLAN_ALTERNATIVE],
          });
        });
    }, [queryClient]),
  );

  const { plans, requestId, isLoading, isFetching, refetch, error } = useGetAlternativePlans(
    mealData?.id ?? "",
    mealTime ?? MealTime.BREAKFAST,
    mealDay,
    filter,
    subFilter,
    fromBrands,
    searchTerm,
    isExtraMeal,
    targetDate,
  );
  console.log("requestId", requestId);

  useEffect(() => {
    if (rawData) {
      setMealPlan(JSON.parse(rawData));
    }
  }, [rawData]);

  // Extract unique meals from API response
  const alternativeMeals = useMemo(() => {
    const data = plans?.meals?.[0]?.mealTime.data ?? [];
    const idSet = new Set<string>();
    const uniqueMeals = data.filter((meal) => {
      if (idSet.has(meal.id ?? "")) {
        return false;
      }
      idSet.add(meal.id ?? "");
      return true;
    });
    return uniqueMeals;
  }, [plans]);

  if (mealPlan === null || mealPlan === undefined || !mealData || !mealTime || !mealDay) {
    return null;
  }

  if (isLoading) {
    return (
      <SwapPlanPageContainer
        ref={pageRef}
        actionButton={
          <SwapPlanSearch
            pageRef={pageRef}
            mealData={mealData}
            mealTime={mealTime}
            mealDay={mealDay}
            mealIndex={mealIndex}
            targetDate={targetDate}
            isExtraMeal={isExtraMeal}
          />
        }
      >
        <SwapPlanFilters mealTime={mealTime} />
        <div className="flex w-full flex-1 flex-col gap-8.5 px-4">
          <IonSkeletonText animated className="w-full flex-2 rounded-xl" />
          <IonSkeletonText animated className="w-full flex-1 rounded-xl" />
        </div>
      </SwapPlanPageContainer>
    );
  }

  if (error) {
    return (
      <SwapPlanPageContainer ref={pageRef}>
        <SwapPlanFilters mealTime={mealTime} />
        <AlternativePlanError retry={refetch} />
      </SwapPlanPageContainer>
    );
  }

  if (plans?.meals?.length === 0 || plans?.meals?.[0]?.mealTime.data.length === 0) {
    return (
      <SwapPlanPageContainer ref={pageRef}>
        <SwapPlanFilters mealTime={mealTime} />
        <NoMealsEmptyState
          mealTime={mealTime}
          selectedSubFilter={subFilter ?? ""}
          refetch={refetch}
        />
      </SwapPlanPageContainer>
    );
  }

  return (
    <SwapPlanPageContainer
      ref={pageRef}
      actionButton={
        <SwapPlanSearch
          pageRef={pageRef}
          mealData={mealData}
          mealTime={mealTime}
          mealDay={mealDay}
          mealIndex={mealIndex}
          targetDate={targetDate}
          isExtraMeal={isExtraMeal}
        />
      }
    >
      <SwapPlanFilters mealTime={mealTime} />

      <SwapPlanSwiper
        alternativeMeals={alternativeMeals}
        mealData={mealData}
        mealTime={mealTime}
        mealDay={mealDay}
        mealIndex={mealIndex}
        targetDate={targetDate}
        requestId={isFetching ? null : requestId}
        resetSignal={swiperResetSignal}
        swipeMappingMode={SWIPE_MAPPING_MODE}
        isExtraMeal={isExtraMeal}
      />
    </SwapPlanPageContainer>
  );
}
